import { Link } from '@tanstack/react-router';
import { ChevronRight, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    formatInboundCallActionLabel,
    formatOutreachResponse,
} from '@apps/web/components/caregivers/caregiver_inbound_call_timeline_utils';
import { ShiftPageDetailsDialog } from '@apps/web/components/dashboard/shift_list_pane';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@apps/web/components/ui/pagination';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Separator } from '@apps/web/components/ui/separator';
import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import {
    getDisconnectAttributionLabel,
    isPhoebeDisconnectAttribution,
} from '@apps/web/components/voice_calls/voice_call_disconnect_attribution';
import {
    formatCallBadgeLabel,
    formatCallDurationLabel,
} from '@apps/web/components/voice_calls/voice_call_format_utils';
import { formatVoiceCallOutreachClientNames } from '@apps/web/components/voice_calls/voice_call_outreach_utils';
import { hasVoiceCallVoicemailBadge } from '@apps/web/components/voice_calls/voice_call_voicemail_utils';
import { isS3RecordingUrl } from '@apps/web/lib/contact_utils';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useVoiceCallRecordingUrls,
    useVoiceCalls,
    type VoiceCallListItem,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type CallTimelineAction = VoiceCallListItem['actions'][number];

type CaregiverCallTimelineTabProps = {
    readonly organizationId: string;
    readonly caregiverId?: string | null;
    readonly clientId?: string | null;
    readonly organizationTimezone: string | null;
    readonly onNavigateToOutreach?: () => void;
};

const CALLS_PAGE_SIZE = 10;
const EMPTY_TIMELINE_ITEMS: Array<VoiceCallListItem> = [];

function wasCallTransferred(call: VoiceCallListItem): boolean {
    if (call.transferred_to_back_office) {
        return true;
    }

    if (call.actions.some((action) => action.type === 'transfer')) {
        return true;
    }

    return (
        call.routing_path === 'direct_transfer' && Boolean(call.transfer_number)
    );
}

function isShiftTimelineAction(action: CallTimelineAction): boolean {
    if (
        action.type !== 'shift_confirmation' &&
        action.type !== 'outreach_response'
    ) {
        return false;
    }

    return Boolean(action.shift_id);
}

function getCallShiftLabel(action: CallTimelineAction): string {
    if (action.shift_patient_name) {
        return action.shift_patient_name;
    }

    if (action.shift_number) {
        return `Patient ${action.shift_number}`;
    }

    return 'Patient';
}

function getCallShiftStatusConfig(action: CallTimelineAction): StatusConfig {
    if (action.type === 'shift_confirmation') {
        switch (action.outcome) {
            case 'confirm':
                return {
                    label: 'Confirmed',
                    dotColor: 'bg-green-500',
                    backgroundColor: 'bg-green-100 dark:bg-green-950/50',
                    textColor: 'text-green-800 dark:text-green-300',
                };
            case 'decline':
                return {
                    label: 'Declined',
                    dotColor: 'bg-red-500',
                    backgroundColor: 'bg-red-100 dark:bg-red-950/50',
                    textColor: 'text-red-800 dark:text-red-300',
                };
            case 'unclear':
                return {
                    label: 'Needs Review',
                    dotColor: 'bg-amber-500',
                    backgroundColor: 'bg-amber-100 dark:bg-amber-950/50',
                    textColor: 'text-amber-800 dark:text-amber-300',
                };
            default:
                return {
                    label: action.outcome
                        ? formatCallBadgeLabel(action.outcome)
                        : 'Updated',
                    dotColor: 'bg-slate-400',
                    backgroundColor: 'bg-muted',
                    textColor: 'text-muted-foreground',
                };
        }
    }

    switch (action.response) {
        case 'interested':
        case 'available_interested':
            return {
                label: 'Available',
                dotColor: 'bg-green-500',
                backgroundColor: 'bg-green-100 dark:bg-green-950/50',
                textColor: 'text-green-800 dark:text-green-300',
            };
        case 'not_interested':
        case 'available_not_interested':
        case 'not_a_fit':
        case 'unavailable':
            return {
                label: 'Unavailable',
                dotColor: 'bg-red-500',
                backgroundColor: 'bg-red-100 dark:bg-red-950/50',
                textColor: 'text-red-800 dark:text-red-300',
            };
        case 'needs_review':
        case 'unknown':
            return {
                label: 'Needs Review',
                dotColor: 'bg-amber-500',
                backgroundColor: 'bg-amber-100 dark:bg-amber-950/50',
                textColor: 'text-amber-800 dark:text-amber-300',
            };
        case 'pending':
        case 'no_response':
            return {
                label: 'Pending',
                dotColor: 'bg-slate-400',
                backgroundColor: 'bg-muted',
                textColor: 'text-muted-foreground',
            };
        default:
            return {
                label: action.response
                    ? formatCallBadgeLabel(action.response)
                    : 'Response Recorded',
                dotColor: 'bg-slate-400',
                backgroundColor: 'bg-muted',
                textColor: 'text-muted-foreground',
            };
    }
}

function ShiftActionRow({
    action,
    callId,
    actionIndex,
    onSelectShift,
}: {
    readonly action: CallTimelineAction;
    readonly callId: string;
    readonly actionIndex: number;
    readonly onSelectShift: (shiftId: string) => void;
}) {
    const shiftLabel = getCallShiftLabel(action);

    return (
        <TableRow key={`${callId}-shift-action-${actionIndex}`}>
            <TableCell className="font-medium whitespace-normal">
                {action.shift_id ? (
                    <button
                        type="button"
                        onClick={() => onSelectShift(action.shift_id!)}
                        className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                    >
                        {shiftLabel}
                    </button>
                ) : (
                    shiftLabel
                )}
            </TableCell>
            <TableCell className="whitespace-normal">
                <StatusBadge status={getCallShiftStatusConfig(action)} />
            </TableCell>
        </TableRow>
    );
}

export function CaregiverCallTimelineTab({
    organizationId,
    caregiverId = null,
    clientId = null,
    organizationTimezone,
    onNavigateToOutreach,
}: CaregiverCallTimelineTabProps) {
    const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([
        undefined,
    ]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pendingPageIndex, setPendingPageIndex] = useState<number | null>(
        null,
    );
    const [expandedTranscriptCallIds, setExpandedTranscriptCallIds] = useState<
        Set<string>
    >(new Set());
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
    const activePageIndex = pendingPageIndex ?? pageIndex;
    const currentCursor = pageCursors[pageIndex];
    const entityId = caregiverId ?? clientId;
    const callsOrganizationId = entityId ? organizationId : null;
    const entityLabel = clientId ? 'client' : 'caregiver';

    const {
        data: callsPage,
        error,
        isLoading,
        isValidating,
    } = useVoiceCalls(
        callsOrganizationId,
        {
            reason: `${entityLabel}-call-timeline-tab`,
        },
        {
            limit: CALLS_PAGE_SIZE,
            cursor: currentCursor,
            caregiver_id: caregiverId ?? undefined,
            client_id: clientId ?? undefined,
            direction: clientId ? 'inbound' : undefined,
        },
    );
    const timelineItems = useMemo(
        () => callsPage?.items ?? EMPTY_TIMELINE_ITEMS,
        [callsPage?.items],
    );
    const isLoadingCurrentPage = isLoading && timelineItems.length === 0;
    const hasPreviousPage = pageIndex > 0;
    const hasNextPage = Boolean(callsPage?.next_cursor) && !isValidating;
    const highestKnownPage = Math.max(pageCursors.length - 1, activePageIndex);
    const maxVisiblePageLinks = 3;
    const maxStartPage = Math.max(
        0,
        highestKnownPage - maxVisiblePageLinks + 1,
    );
    const startPage = Math.min(
        Math.max(0, activePageIndex - Math.floor(maxVisiblePageLinks / 2)),
        maxStartPage,
    );
    const endPage = Math.min(
        highestKnownPage,
        startPage + maxVisiblePageLinks - 1,
    );
    const visiblePageSlots = Array.from(
        { length: maxVisiblePageLinks },
        (_, index) => {
            const pageNumber = startPage + index;
            if (pageNumber > endPage) {
                return null;
            }
            return pageNumber;
        },
    );

    useEffect(() => {
        setPageCursors([undefined]);
        setPageIndex(0);
        setPendingPageIndex(null);
        setExpandedTranscriptCallIds(new Set());
        setSelectedShiftId(null);
    }, [organizationId, caregiverId, clientId]);

    useEffect(() => {
        if (pendingPageIndex === null) {
            return;
        }
        if (pendingPageIndex !== pageIndex) {
            setPendingPageIndex(null);
            return;
        }
        if (!isValidating) {
            setPendingPageIndex(null);
        }
    }, [isValidating, pageIndex, pendingPageIndex]);

    useEffect(() => {
        if (isValidating) {
            return;
        }

        const nextCursor = callsPage?.next_cursor;
        setPageCursors((current) => {
            const nextPageIndex = pageIndex + 1;
            if (nextCursor) {
                if (current[nextPageIndex] === nextCursor) {
                    return current;
                }

                const next = current.slice();
                next[nextPageIndex] = nextCursor;
                return next;
            }

            if (current.length <= nextPageIndex) {
                return current;
            }

            return current.slice(0, nextPageIndex);
        });
    }, [callsPage?.next_cursor, isValidating, pageIndex]);

    const recordingVoiceCallIds = useMemo(() => {
        const callIds = timelineItems
            .filter((item) => isS3RecordingUrl(item.recording_url))
            .map((item) => item.id);

        return callIds.length > 0 ? callIds : null;
    }, [timelineItems]);

    const { data: recordingUrls } = useVoiceCallRecordingUrls(
        organizationId,
        recordingVoiceCallIds,
    );

    const toggleTranscript = useCallback((callId: string) => {
        setExpandedTranscriptCallIds((previousValue) => {
            const nextValue = new Set(previousValue);
            if (nextValue.has(callId)) {
                nextValue.delete(callId);
            } else {
                nextValue.add(callId);
            }
            return nextValue;
        });
    }, []);

    const handleGoToOlderPage = () => {
        const nextCursor = callsPage?.next_cursor;
        if (!nextCursor || isValidating) {
            return;
        }
        const targetPageIndex = pageIndex + 1;

        setPageCursors((current) => {
            const next = current.slice();
            next[targetPageIndex] = nextCursor;
            return next;
        });
        setPendingPageIndex(targetPageIndex);
        setPageIndex(targetPageIndex);
    };

    const handleGoToNewerPage = () => {
        if (!hasPreviousPage || isValidating) {
            return;
        }
        const targetPageIndex = Math.max(0, pageIndex - 1);
        setPendingPageIndex(targetPageIndex);
        setPageIndex(targetPageIndex);
    };

    if (!entityId) {
        return (
            <p className="text-muted-foreground text-sm">
                Select a {entityLabel} to view call history.
            </p>
        );
    }

    if (isLoadingCurrentPage) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading call history…
            </div>
        );
    }

    if (error && timelineItems.length === 0) {
        return (
            <p className="text-sm text-red-600 dark:text-red-400">
                Unable to load call history.
            </p>
        );
    }

    if (timelineItems.length === 0) {
        return (
            <p className="text-muted-foreground text-sm">
                No calls found for this {entityLabel}.
            </p>
        );
    }

    const timezone = organizationTimezone ?? 'UTC';

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <ScrollArea
                className="min-h-0 flex-1"
                forceMountScrollbar={true}
                scrollbarClassName="data-[state=hidden]:opacity-100"
            >
                <div className="space-y-3 pr-3">
                    {isValidating && timelineItems.length > 0 && (
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading page…
                        </div>
                    )}
                    {timelineItems.map((call) => {
                        const timestamp = call.started_at ?? call.created_at;
                        const durationLabel = formatCallDurationLabel(
                            call.duration_ms,
                        );
                        const isInboundCall = call.direction === 'inbound';
                        const transferred = wasCallTransferred(call);
                        const hasVoicemailFlag =
                            hasVoiceCallVoicemailBadge(call);
                        const outreachClientNamesText =
                            formatVoiceCallOutreachClientNames(
                                call.outreach_client_names,
                            );
                        const disconnectAttribution =
                            call.disconnect_attribution;
                        const disconnectAttributionLabel =
                            getDisconnectAttributionLabel(
                                disconnectAttribution,
                            );
                        const isPhoebeDisconnect =
                            isPhoebeDisconnectAttribution(
                                disconnectAttribution,
                            );
                        const isTranscriptExpanded =
                            expandedTranscriptCallIds.has(call.id);
                        const shiftActions = call.actions.filter(
                            isShiftTimelineAction,
                        );
                        const nonShiftActions = call.actions.filter(
                            (action) => {
                                if (
                                    action.type === 'transfer' ||
                                    action.type === 'shift_confirmation'
                                ) {
                                    return false;
                                }

                                return !isShiftTimelineAction(action);
                            },
                        );

                        return (
                            <div
                                key={call.id}
                                className="bg-background rounded-md border p-3"
                            >
                                <div className="flex min-w-0 items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-2 flex min-w-0 items-start justify-between gap-2">
                                            <p className="text-sm font-medium">
                                                {formatDateAndTime(
                                                    timestamp,
                                                    timezone,
                                                )}
                                            </p>
                                            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                                                <Badge variant="outline">
                                                    {isInboundCall
                                                        ? 'Inbound'
                                                        : 'Outbound'}
                                                </Badge>
                                                {call.transferred_to_back_office && (
                                                    <Badge variant="secondary">
                                                        Transferred
                                                    </Badge>
                                                )}
                                                {hasVoicemailFlag && (
                                                    <Badge variant="secondary">
                                                        Voicemail
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        {(durationLabel ||
                                            (clientId &&
                                                (call.caller_name ||
                                                    call.caller_phone)) ||
                                            disconnectAttributionLabel ||
                                            (transferred &&
                                                call.transfer_number) ||
                                            (call.direction === 'outbound' &&
                                                call.callout_id)) && (
                                            <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                                {durationLabel && (
                                                    <span>
                                                        Duration:{' '}
                                                        {durationLabel}
                                                    </span>
                                                )}
                                                {clientId &&
                                                    (call.caller_name ||
                                                        call.caller_phone) && (
                                                        <span>
                                                            Caller:{' '}
                                                            {call.caller_name ??
                                                                call.caller_phone}
                                                            {call.caller_name &&
                                                                call.caller_phone && (
                                                                    <React.Fragment>
                                                                        {' · '}
                                                                        <span className="font-mono">
                                                                            {
                                                                                call.caller_phone
                                                                            }
                                                                        </span>
                                                                    </React.Fragment>
                                                                )}
                                                        </span>
                                                    )}
                                                {disconnectAttributionLabel ? (
                                                    <span
                                                        className={cn(
                                                            isPhoebeDisconnect &&
                                                                'font-medium text-red-700 dark:text-red-400',
                                                        )}
                                                    >
                                                        Ended by:{' '}
                                                        {
                                                            disconnectAttributionLabel
                                                        }
                                                    </span>
                                                ) : null}
                                                {transferred &&
                                                    call.transfer_number && (
                                                        <span>
                                                            Transferred to{' '}
                                                            <span className="font-mono">
                                                                {
                                                                    call.transfer_number
                                                                }
                                                            </span>
                                                        </span>
                                                    )}
                                                {call.direction ===
                                                    'outbound' &&
                                                    call.callout_id && (
                                                        <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5">
                                                            <span className="whitespace-nowrap">
                                                                Associated
                                                                outreach:
                                                            </span>
                                                            {outreachClientNamesText && (
                                                                <span className="min-w-0 break-words">
                                                                    {
                                                                        outreachClientNamesText
                                                                    }
                                                                </span>
                                                            )}
                                                            <Link
                                                                className="whitespace-nowrap text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                                                                to="/outreaches/$callout-id"
                                                                params={{
                                                                    'callout-id':
                                                                        call.callout_id,
                                                                }}
                                                                onClick={() => {
                                                                    onNavigateToOutreach?.();
                                                                }}
                                                            >
                                                                View outreach
                                                            </Link>
                                                        </span>
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {(shiftActions.length > 0 ||
                                    nonShiftActions.length > 0) && (
                                    <div className="mt-3 space-y-3">
                                        {shiftActions.length > 0 && (
                                            <div className="rounded-lg border">
                                                <Table className="table-fixed">
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="w-1/2">
                                                                Patient
                                                            </TableHead>
                                                            <TableHead className="w-1/2">
                                                                Status
                                                            </TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {shiftActions.map(
                                                            (
                                                                action,
                                                                actionIndex,
                                                            ) => (
                                                                <ShiftActionRow
                                                                    key={`${call.id}-shift-action-${actionIndex}`}
                                                                    action={
                                                                        action
                                                                    }
                                                                    callId={
                                                                        call.id
                                                                    }
                                                                    actionIndex={
                                                                        actionIndex
                                                                    }
                                                                    onSelectShift={
                                                                        setSelectedShiftId
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        )}
                                        {nonShiftActions.length > 0 && (
                                            <div className="space-y-1">
                                                {nonShiftActions.map(
                                                    (action, actionIndex) => {
                                                        if (
                                                            action.type ===
                                                            'outreach_response'
                                                        ) {
                                                            return (
                                                                <p
                                                                    key={`${call.id}-action-${actionIndex}`}
                                                                    className="text-muted-foreground text-xs"
                                                                >
                                                                    {formatOutreachResponse(
                                                                        action.response,
                                                                    )}
                                                                </p>
                                                            );
                                                        }

                                                        return (
                                                            <p
                                                                key={`${call.id}-action-${actionIndex}`}
                                                                className="text-muted-foreground text-xs break-all"
                                                            >
                                                                {formatInboundCallActionLabel(
                                                                    action,
                                                                )}
                                                            </p>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {call.call_summary && (
                                    <p className="mt-3 text-sm leading-relaxed">
                                        {call.call_summary}
                                    </p>
                                )}
                                {call.transcript_preview && (
                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                                            onClick={() =>
                                                toggleTranscript(call.id)
                                            }
                                        >
                                            <ChevronRight
                                                className={cn(
                                                    'h-3 w-3 transition-transform',
                                                    isTranscriptExpanded
                                                        ? 'rotate-90'
                                                        : 'rotate-0',
                                                )}
                                            />
                                            <span>
                                                {isTranscriptExpanded
                                                    ? 'Hide transcript'
                                                    : 'Show transcript'}
                                            </span>
                                        </button>
                                        {isTranscriptExpanded && (
                                            <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
                                                {call.transcript_preview}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {call.recording_url && (
                                    <div className="mt-3">
                                        <VoiceCallAudioPlayer
                                            voiceCall={{
                                                id: call.id,
                                                recording_url:
                                                    call.recording_url,
                                            }}
                                            prefetchedRecordingUrl={
                                                recordingUrls?.[call.id]
                                            }
                                            className="h-10 w-full focus:outline-none"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </ScrollArea>
            {(highestKnownPage > 0 || hasPreviousPage || hasNextPage) && (
                <React.Fragment>
                    <Separator className="my-3" />
                    <Pagination className="w-full">
                        <PaginationContent className="w-full justify-between">
                            <PaginationItem>
                                <PaginationPrevious
                                    href="#"
                                    aria-disabled={
                                        !hasPreviousPage || isValidating
                                    }
                                    className={cn(
                                        (!hasPreviousPage || isValidating) &&
                                            'pointer-events-none opacity-50',
                                    )}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        handleGoToNewerPage();
                                    }}
                                />
                            </PaginationItem>
                            <PaginationItem className="mx-2 flex-1">
                                <div className="mx-auto grid w-[104px] grid-cols-3 gap-1">
                                    {visiblePageSlots.map(
                                        (pageNumber, slotIndex) => {
                                            if (pageNumber === null) {
                                                return (
                                                    <span
                                                        aria-hidden={true}
                                                        className="h-8 w-8"
                                                        key={`empty-page-slot-${startPage + slotIndex}`}
                                                    />
                                                );
                                            }

                                            const isCurrentPage =
                                                pageNumber === activePageIndex;
                                            const canNavigateToPage =
                                                pageNumber ===
                                                    activePageIndex ||
                                                pageNumber < pageCursors.length;

                                            return (
                                                <PaginationLink
                                                    key={pageNumber}
                                                    href="#"
                                                    isActive={isCurrentPage}
                                                    size="icon"
                                                    aria-disabled={
                                                        !canNavigateToPage
                                                    }
                                                    className={cn(
                                                        'h-8 w-8 transition-colors duration-100',
                                                        !canNavigateToPage &&
                                                            'pointer-events-none opacity-50',
                                                    )}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        if (
                                                            !canNavigateToPage ||
                                                            isValidating
                                                        ) {
                                                            return;
                                                        }
                                                        setPendingPageIndex(
                                                            pageNumber,
                                                        );
                                                        setPageIndex(
                                                            pageNumber,
                                                        );
                                                    }}
                                                >
                                                    {pageNumber + 1}
                                                </PaginationLink>
                                            );
                                        },
                                    )}
                                </div>
                            </PaginationItem>
                            <PaginationItem>
                                <PaginationNext
                                    href="#"
                                    aria-disabled={!hasNextPage || isValidating}
                                    className={cn(
                                        (!hasNextPage || isValidating) &&
                                            'pointer-events-none opacity-50',
                                    )}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        handleGoToOlderPage();
                                    }}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </React.Fragment>
            )}
            <ShiftPageDetailsDialog
                organizationId={organizationId}
                shiftId={selectedShiftId}
                isOpen={selectedShiftId !== null}
                onClose={() => setSelectedShiftId(null)}
                onBeforeNavigateToOutreach={onNavigateToOutreach}
            />
        </div>
    );
}
